import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel,
  TablePagination, TableContainer, Container, Typography, Alert, TextField,
  MenuItem, Button, Box, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Snackbar, Checkbox, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import walletApi from '../../api/wallet';
import { formatDate, formatValue, getAssetClasses, getAssetInstrumentTypesByClass } from '../../utils/utils';
import OperationsForm from './OperationsForm';
import { getErrorMessage } from '../../utils/errors';
import useSorting from '../../utils/useSorting';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const assetClassOptions = [
  { value: '', label: 'Classe' },
  ...getAssetClasses(),
];

const operationTypes = [
  { value: '', label: 'Tipo' },
  { value: 'buy', label: 'Compra' },
  { value: 'sell', label: 'Venda' },
];

const columns = [
  { id: 'select', label: '', sortable: false, width: 50 },
  { id: 'Asset.Name', label: 'Ativo', sortable: true },
  { id: 'Asset.Class', label: 'Classe', sortable: true },
  { id: 'Asset.InstrumentType', label: 'Tipo', sortable: true, hideOnMobile: true },
  { id: 'OperationType', label: 'Op', sortable: true },
  { id: 'Quantity', label: 'Qtd', sortable: true },
  { id: 'UnitValue', label: 'Unit.', sortable: true, hideOnMobile: true },
  { id: 'TotalValue', label: 'Total', sortable: true },
  { id: 'Rate', label: 'Taxa', sortable: false, hideOnLg: true },
  { id: 'Maturity', label: 'Venc.', sortable: true, hideOnLg: true },
  { id: 'OperationDate', label: 'Data', sortable: true, hideOnSm: true },
  { id: 'actions', label: '', sortable: false },
];

const OperationsList = () => {
  const [operations, setOperations] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, limit: 10, totalCount: 0 });
  const [error, setError] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [filters, setFilters] = useState({
    assetName: '',
    assetClass: '',
    instrumentType: '',
    startDate: '',
    endDate: '',
    operationType: '',
  });
  const [displayCurrency, setDisplayCurrency] = useState('BRL');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const debouncedAssetName = useDebounce(filters.assetName, 400);

  const valueTransformers = {
    'Asset.Name': (op) => op.Asset?.Name || '',
    'Asset.Class': (op) => op.Asset?.Class || '',
    'Asset.InstrumentType': (op) => op.Asset?.InstrumentType || '',
    Quantity: (op) => parseFloat(op.Quantity) || 0,
    TotalValue: (op) => parseFloat(op.FiatFixed?.find((f) => f.Currency === displayCurrency)?.Value) || 0,
  };

  const { orderBy, order, sortedData, handleRequestSort } = useSorting(
    operations,
    'OperationDate',
    'desc',
    valueTransformers
  );

  const fetchAssets = useCallback(async () => {
    try {
      const page = pagination.page + 1;
      const limit = pagination.limit;

      const params = [
        `Page=${page}`,
        `Limit=${limit}`,
        debouncedAssetName && `AssetName=${encodeURIComponent(debouncedAssetName)}`,
        filters.assetClass && `AssetClass=${encodeURIComponent(filters.assetClass)}`,
        filters.instrumentType && `InstrumentType=${encodeURIComponent(filters.instrumentType)}`,
        filters.startDate && `StartDate=${encodeURIComponent(filters.startDate)}`,
        filters.endDate && `EndDate=${encodeURIComponent(filters.endDate)}`,
        filters.operationType && `OperationType=${encodeURIComponent(filters.operationType)}`,
      ].filter(Boolean).join('&');

      const response = await walletApi.get(`operations?${params}`);
      setOperations(response.data.Operations || []);
      setPagination((prev) => ({ ...prev, totalCount: response.data.TotalCount || 0 }));
      setError('');
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao buscar operacoes.'));
      setErrorOpen(true);
    }
  }, [pagination.page, pagination.limit, debouncedAssetName, filters]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handlePaginationChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    setPagination({ page: 0, limit: parseInt(event.target.value, 10), totalCount: pagination.totalCount });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'assetClass') {
      setFilters((prev) => ({ ...prev, assetClass: value, instrumentType: '' }));
      setPagination((prev) => ({ ...prev, page: 0 }));
    } else if (name !== 'assetName') {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 0 }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClearFilters = () => {
    setFilters({
      assetName: '',
      assetClass: '',
      instrumentType: '',
      startDate: '',
      endDate: '',
      operationType: '',
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenDeleteDialog = (operation) => {
    setOperationToDelete(operation);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOperationToDelete(null);
  };

  const handleDeleteOperation = async () => {
    if (!operationToDelete) return;
    try {
      await walletApi.delete(`/operations/${operationToDelete.ID}`);
      setDeleteDialogOpen(false);
      setOperationToDelete(null);
      fetchAssets();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao deletar operacao.'));
      setErrorOpen(true);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(sortedData.map((op) => op.ID));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const calculateTotal = () => {
    const itemsToSum = selectedIds.length > 0
      ? operations.filter((op) => selectedIds.includes(op.ID))
      : operations;
    return itemsToSum.reduce((sum, op) => {
      const value = parseFloat(op.FiatFixed?.find((f) => f.Currency === displayCurrency)?.Value) || 0;
      return sum + value;
    }, 0);
  };

  const hasActiveFilters =
    filters.assetName || filters.assetClass || filters.instrumentType ||
    filters.startDate || filters.endDate || filters.operationType;

  const getOperationTypeChip = (type) => {
    if (type === 'buy') {
      return <Chip label="C" size="small" color="success" sx={{ minWidth: 24, height: 20, fontSize: '0.7rem' }} />;
    }
    if (type === 'sell') {
      return <Chip label="V" size="small" color="error" sx={{ minWidth: 24, height: 20, fontSize: '0.7rem' }} />;
    }
    return <Chip label="-" size="small" sx={{ minWidth: 24, height: 20, fontSize: '0.7rem' }} />;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Operacoes</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Nova
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Ativo..."
          name="assetName"
          value={filters.assetName}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 120 }}
        />
        <TextField
          select
          name="assetClass"
          value={filters.assetClass}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 110 }}
          SelectProps={{ displayEmpty: true }}
        >
          {assetClassOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          name="instrumentType"
          value={filters.instrumentType}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 120 }}
          disabled={!filters.assetClass}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">Instrumento</MenuItem>
          {getAssetInstrumentTypesByClass(filters.assetClass).map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          name="operationType"
          value={filters.operationType}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 90 }}
          SelectProps={{ displayEmpty: true }}
        >
          {operationTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          value={displayCurrency}
          onChange={(e) => setDisplayCurrency(e.target.value)}
          size="small"
          sx={{ width: 80 }}
        >
          <MenuItem value="BRL">BRL</MenuItem>
          <MenuItem value="USD">USD</MenuItem>
        </TextField>
        <TextField
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 140 }}
        />
        <TextField
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 140 }}
        />
        {hasActiveFilters && (
          <Button variant="text" size="small" onClick={handleClearFilters} sx={{ textTransform: 'none' }}>
            Limpar
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <TableContainer sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    width: column.width,
                    display: column.hideOnMobile
                      ? { xs: 'none', md: 'table-cell' }
                      : column.hideOnSm
                      ? { xs: 'none', sm: 'table-cell' }
                      : column.hideOnLg
                      ? { xs: 'none', lg: 'table-cell' }
                      : 'table-cell',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                  padding={column.id === 'select' ? 'checkbox' : 'normal'}
                >
                  {column.id === 'select' ? (
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < sortedData.length}
                      checked={sortedData.length > 0 && selectedIds.length === sortedData.length}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  ) : column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((op) => {
                const quantity = parseFloat(op.Quantity) || 0;
                const totalValue = parseFloat(op.FiatFixed?.find((f) => f.Currency === displayCurrency)?.Value) || 0;
                const unitValue = op.UnitValue ? parseFloat(op.UnitValue) : (quantity > 0 ? totalValue / quantity : 0);

                return (
                  <TableRow key={op.ID} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(op.ID)}
                        onChange={() => handleSelectOne(op.ID)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{op.Asset?.Name || '-'}</TableCell>
                    <TableCell>
                      <Chip label={op.Asset?.Class || '-'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.875rem' }}>
                      {op.Asset?.InstrumentType || '-'}
                    </TableCell>
                    <TableCell>{getOperationTypeChip(op.OperationType)}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{quantity}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.875rem' }}>
                      {formatValue(unitValue, displayCurrency)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      {formatValue(totalValue, displayCurrency)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '0.875rem' }}>
                      {op.Rate || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '0.875rem' }}>
                      {op.Maturity ? formatDate(op.Maturity) : '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.875rem' }}>
                      {formatDate(op.OperationDate)}
                    </TableCell>
                    <TableCell>
                      <IconButton color="error" size="small" onClick={() => handleOpenDeleteDialog(op)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhuma operacao encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {sortedData.length > 0 && (
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell colSpan={6} sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.875rem' }}>
                  {selectedIds.length > 0 ? `${selectedIds.length} selecionadas` : `${sortedData.length} operacoes`}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {formatValue(calculateTotal(), displayCurrency)}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          onPageChange={handlePaginationChange}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={handleLimitChange}
          labelRowsPerPage="Por pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Exclusao</DialogTitle>
        <DialogContent>
          Tem certeza que deseja deletar esta operacao?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button onClick={handleDeleteOperation} color="error" variant="contained">Deletar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <OperationsForm onCreate={() => {
            setCreateDialogOpen(false);
            fetchAssets();
          }} />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OperationsList;
